<template lang="pug">
div
  public-navbar
  // Hero Section
  v-parallax(
    src="https://images.unsplash.com/photo-1556795573-1f36b83594f6?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    :height="$vuetify.breakpoint.smAndDown ? 250 : 350"
        class="parallax-header"
  )
    .parallax-overlay
    v-container(fill-height)
      v-row(align="center" justify="center")
        v-col(cols="12" md="8" class="text-center")
          h1.text-h2.font-weight-bold.white--text.text-shadow Blog Gastronómico
  // Article Content
  v-container(class="py-12")
    v-row(justify="center")
      v-col(cols="12" md="8")
        template(v-if="article")
          // Article Header
          .text-center.mb-8
            h1.text-h3.font-weight-bold.mb-4 {{ article.title }}
            .d-flex.justify-center.align-center.mb-4
              v-avatar(size="40" class="mr-2")
                v-img(:src="article.author?.photo || '/img/default-avatar.png'")
              .text-subtitle-1.mr-4 {{ article.author?.name }}
              v-divider(vertical class="mx-4")
              .text-subtitle-1.grey--text
                v-icon(small class="mr-1") mdi-calendar
                | {{ formatDate(article?.publishedAt) }}
            
            // Category and Tags
            .d-flex.justify-center.align-center.flex-wrap
              v-chip(
                :color="getCategoryColor(article.category)"
                label
                class="mr-2 mb-2"
              ) {{ article.category }}
              v-chip(
                v-for="tag in article.tags"
                :key="tag"
                class="mr-2 mb-2"
                @click="goToTag(tag)"
              ) {{ tag }}

          // Hero Image
          v-img.rounded-lg.mb-8(
            :src="article.image"
            :aspect-ratio="16/9"
            gradient="to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5)"
          )

          // Article Content
          .article-content.mb-8(v-html="formattedContent")

          v-divider.mb-8

          // Social Share
          .d-flex.justify-center.mb-8
            v-btn(
              v-for="social in socialShare"
              :key="social.network"
              :color="social.color"
              dark
              class="mx-2"
              @click="shareArticle(social.network)"
            )
              v-icon(left) {{ social.icon }}
              | {{ social.text }}

          // Comments Section
          v-card(outlined)
            v-card-title Comentarios
            v-card-text
              template(v-if="article.comments.length")
                v-list
                  v-list-item(
                    v-for="comment in article.comments"
                    :key="comment._id"
                  )
                    v-list-item-avatar
                      v-img(:src="comment.user?.photo || '/img/default-avatar.png'")
                    v-list-item-content
                      v-list-item-title {{ comment.user?.name }}
                      v-list-item-subtitle.caption.grey--text
                        | {{ formatDate(comment?.createdAt) }}
                      .body-2.mt-2 {{ comment.content }}
              template(v-else)
                .text-center.grey--text No hay comentarios aún

            v-card-actions(v-if="$store.getters['auth/isAuthenticated']")
              v-textarea(
                v-model="newComment"
                label="Escribe un comentario"
                rows="3"
                outlined
                hide-details
              )
              v-btn(
                color="primary"
                :disabled="!newComment"
                @click="addComment"
                class="ml-4"
              ) Comentar

          // Related Articles
          template(v-if="relatedArticles.length")
            h2.text-h5.font-weight-bold.mt-12.mb-6 Artículos Relacionados
            v-row
              v-col(
                v-for="related in relatedArticles"
                :key="related._id"
                cols="12"
                sm="6"
                md="4"
              )
                v-hover(v-slot="{ hover }")
                  v-card(
                    :elevation="hover ? 4 : 1"
                    :class="{'on-hover': hover}"
                    @click="viewArticle(related)"
                  )
                    v-img(
                      :src="related.image"
                      height="200"
                      :class="{'on-hover': hover}"
                    )
                    v-card-title {{ related.title }}
                    v-card-subtitle
                      .d-flex.align-center
                        v-icon(small) mdi-calendar
                        span.ml-1 {{ formatDate(related?.publishedAt) }}

        // Loading State
        template(v-else-if="loading")
          v-skeleton-loader(
            type="article, paragraph, paragraph, paragraph"
            class="mb-4"
          )

        // Error State
        template(v-else)
          v-alert(
            type="error"
            text
            class="mb-4"
          ) Artículo no encontrado
</template>

<script>
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import PublicNavbar from '@/components/layout/PublicNavbar.vue';

export default {
  name: 'BlogArticle',
  components: {
    PublicNavbar
  },

  data: () => ({
    mobileMenuOpen: false,
    loading: true,
    article: null,
    newComment: '',
    socialShare: [
      { network: 'facebook', icon: 'mdi-facebook', color: '#1877F2', text: 'Compartir' },
      { network: 'twitter', icon: 'mdi-twitter', color: '#1DA1F2', text: 'Tweet' },
      { network: 'whatsapp', icon: 'mdi-whatsapp', color: '#25D366', text: 'WhatsApp' }
    ]
  }),

  computed: {
    formattedContent() {
      if (!this.article?.content) return '';
      const rawHtml = this.article.content;
      return rawHtml;
    },

    relatedArticles() {
      if (!this.article) return [];

      return this.$store.state.blog.articles
        .filter(a => 
          a._id !== this.article._id && 
          (a.category === this.article.category || 
            a.tags.some(t => this.article.tags.includes(t)))
        )
        .slice(0, 3);
    }
  },

  methods: {
    formatDate(date) {
      if (!date) return '';
      return format(new Date(date), "d 'de' MMMM, yyyy", { locale: es });
    },

    getCategoryColor(category) {
      const colors = {
        'Nuevas Aperturas': 'success',
        'Eventos Especiales': 'primary',
        'Menús Destacados': 'warning',
        'Entrevistas': 'info',
        'Recetas': 'purple',
        'Tips & Consejos': 'orange'
      };
      return colors[category] || 'grey';
    },

    goToTag(tag) {
      this.$router.push({
        name: 'Blog',
        query: { tag }
      })
    },

    async loadArticle() {
      this.loading = true;
      try {
        const { data } = await this.$http.get(`/blog/${this.$route.params.slug}`);
        this.article = data;
      } catch (error) {
        this.$toast.error('Error al cargar el artículo');
      } finally {
        this.loading = false;
      }
    },

    viewArticle(article) {
      this.$router.push(`/blog/${article.slug}`);
    },

    shareArticle(network) {
      const url = window.location.href;
      const text = this.article.title;

      const shareUrls = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`
      };

      window.open(shareUrls[network], '_blank');
    },

    async addComment() {
      if (!this.newComment.trim()) return;

      try {
        await this.$store.dispatch('blog/addComment', {
          articleId: this.article._id,
          content: this.newComment
        });
        
        this.newComment = '';
        await this.loadArticle(); // Reload to get updated comments
        this.$toast.success('Comentario agregado');
      } catch (error) {
        this.$toast.error('Error al agregar el comentario');
      }
    }
  },

  async created() {
    await this.loadArticle();
  },

  watch: {
    '$route.params.slug'() {
      this.loadArticle();
    }
  }
};
</script>

<style scoped>
.article-content {
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(0, 0, 0, 0.87);
}

.article-content :deep(h2) {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 2rem 0 1rem;
}

.article-content :deep(h3) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem;
}

.article-content :deep(p) {
  margin-bottom: 1.5rem;
}

.article-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 2rem 0;
}

.article-content :deep(blockquote) {
  border-left: 4px solid var(--v-primary-base);
  margin: 2rem 0;
  padding: 1rem 0 1rem 2rem;
  font-style: italic;
  background: rgba(0, 0, 0, 0.03);
}

.article-content :deep(pre) {
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 2rem 0;
}

.article-content :deep(code) {
  background: #f5f5f5;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9em;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin: 1.5rem 0;
  padding-left: 2rem;
}

.article-content :deep(li) {
  margin-bottom: 0.5rem;
}

.article-content :deep(a) {
  color: var(--v-primary-base);
  text-decoration: none;
}

.article-content :deep(a:hover) {
  text-decoration: underline;
}

@media (max-width: 600px) {
  .v-btn {
    min-height: 44px !important;
  }
  .v-input__append-outer {
      display: none!important;
  }
  .v-chip {
    height: 30px !important;
  }
  h1.text-h2.font-weight-bold.white--text.text-shadow {
    font-size: 2rem!important;
    padding-top: 80px!important;
  }
  p.text-h6.white--text.text-shadow {
      font-size: 1rem !important;
  }
}
header.v-sheet.theme--light.elevation-1.v-toolbar.v-toolbar--absolute.v-app-bar {
  box-shadow: none !important;
  border: none;
}
.v-app-bar a {
    text-decoration: none;
}
.theme--light.v-app-bar.v-toolbar.v-sheet {
    background-color: #f5f5f500;
  }
.parallax-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
  z-index: 1;
}
.text-center.col-md-8.col-12 {
    z-index: 4;
}
    </style>